import { atom } from "jotai";
import { atomWithImmer } from "jotai-immer";

import { ApiError } from "@frontend/lib/http";

import { projectApi } from "../services";
import type {
  AmendmentImpact,
  ClosureWeek,
  ClosureWeekYear,
  PrefillSource,
  ValidatedHoursWeek,
} from "../types";

export type ClosureWeeksState = {
  projectId: number;
  year: number;
  weeks: ClosureWeek[];
  amendments: AmendmentImpact[];
  contractClosureWeeksCount: number | null;
  selected: number[];
  holidayLinks: Record<number, number>;
  saved: { selected: number[] };
  loading: boolean;
  saving: boolean;
  error: string | null;
  blockingWeeks: ValidatedHoursWeek[];
  prefilled: number[];
};

export const initialClosureWeeksState: ClosureWeeksState = {
  projectId: 0,
  year: new Date().getFullYear(),
  weeks: [],
  amendments: [],
  contractClosureWeeksCount: null,
  selected: [],
  holidayLinks: {},
  saved: { selected: [] },
  loading: true,
  saving: false,
  error: null,
  blockingWeeks: [],
  prefilled: [],
};

export const closureWeeksAtom = atomWithImmer<ClosureWeeksState>(
  initialClosureWeeksState,
);

export const loadedProjectIdAtom = atom(
  (get) => get(closureWeeksAtom).projectId,
);

export const selectedCountAtom = atom(
  (get) => get(closureWeeksAtom).selected.length,
);

export const remainingAtom = atom((get) => {
  const { contractClosureWeeksCount, selected } = get(closureWeeksAtom);

  return (contractClosureWeeksCount ?? 0) - selected.length;
});

export const overrunAtom = atom((get) => get(remainingAtom) < 0);

export const dirtyAtom = atom((get) => {
  const { selected, saved } = get(closureWeeksAtom);

  return (
    selected.length !== saved.selected.length ||
    selected.some((week) => !saved.selected.includes(week))
  );
});

export const prefilledValidatedAtom = atom((get) => {
  const { weeks, selected, prefilled } = get(closureWeeksAtom);

  return weeks.filter(
    (week) =>
      null !== week.validatedHours &&
      prefilled.includes(week.number) &&
      selected.includes(week.number),
  );
});

export const impactedAmendmentsAtom = atom((get) =>
  get(closureWeeksAtom).amendments.filter((amendment) => amendment.changed),
);

export const loadClosureWeeksAtom = atom(
  null,
  async (_get, set, projectId: number, year: number) => {
    set(closureWeeksAtom, (state) => {
      state.projectId = projectId;
      state.year = year;
      state.loading = true;
      state.error = null;
    });

    try {
      const loaded = await projectApi.fetchClosureWeeks(projectId, year);

      set(closureWeeksAtom, (state) => {
        apply(state, loaded);
        state.saved = { selected: closedWeeksOf(loaded) };
        state.loading = false;
      });
    } catch (error: unknown) {
      set(closureWeeksAtom, (state) => {
        state.loading = false;
        state.error = message(error);
      });
    }
  },
);

export const prefillClosureWeeksAtom = atom(
  null,
  async (get, set, source: PrefillSource) => {
    const { projectId, year } = get(closureWeeksAtom);

    set(closureWeeksAtom, (state) => {
      state.loading = true;
      state.error = null;
    });

    try {
      const proposed = await projectApi.fetchClosureWeeks(
        projectId,
        year,
        source,
      );

      set(closureWeeksAtom, (state) => {
        state.prefilled = closedWeeksOf(proposed).filter(
          (week) => !state.selected.includes(week),
        );
        state.weeks = proposed.weeks;
        state.amendments = proposed.amendments;
        state.holidayLinks = {
          ...state.holidayLinks,
          ...holidayLinksOf(proposed),
        };
        state.selected = [
          ...new Set([...state.selected, ...closedWeeksOf(proposed)]),
        ].sort((a, b) => a - b);
        state.loading = false;
      });
    } catch (error: unknown) {
      set(closureWeeksAtom, (state) => {
        state.loading = false;
        state.error = message(error);
      });
    }
  },
);

export const toggleWeekAtom = atom(null, (_get, set, weekNumber: number) => {
  set(closureWeeksAtom, (state) => {
    state.selected = state.selected.includes(weekNumber)
      ? state.selected.filter((week) => week !== weekNumber)
      : [...state.selected, weekNumber].sort((a, b) => a - b);
  });
});

export const saveClosureWeeksAtom = atom(
  null,
  async (get, set): Promise<boolean> => {
    const { projectId, year, selected, holidayLinks } = get(closureWeeksAtom);

    set(closureWeeksAtom, (state) => {
      state.saving = true;
      state.error = null;
      state.blockingWeeks = [];
    });

    try {
      const saved = await projectApi.saveClosureWeeks(projectId, year, {
        weeks: selected.map((week) => ({
          number: week,
          holidayId: holidayLinks[week] ?? null,
        })),
      });

      set(closureWeeksAtom, (state) => {
        apply(state, saved);
        state.saved = { selected: closedWeeksOf(saved) };
        state.saving = false;
      });

      return true;
    } catch (error: unknown) {
      set(closureWeeksAtom, (state) => {
        state.saving = false;
        state.error = message(error);
        state.blockingWeeks = blockingWeeksOf(error);
      });

      return false;
    }
  },
);

function apply(state: ClosureWeeksState, year: ClosureWeekYear): void {
  state.prefilled = [];
  state.weeks = year.weeks;
  state.amendments = year.amendments;
  state.contractClosureWeeksCount = year.contractClosureWeeksCount;
  state.selected = closedWeeksOf(year);
  state.holidayLinks = holidayLinksOf(year);
}

function closedWeeksOf(year: ClosureWeekYear): number[] {
  return year.weeks.filter((week) => week.closed).map((week) => week.number);
}

function holidayLinksOf(year: ClosureWeekYear): Record<number, number> {
  const links: Record<number, number> = {};

  for (const week of year.weeks) {
    if (null !== week.holidayId) {
      links[week.number] = week.holidayId;
    }
  }

  return links;
}

function message(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function blockingWeeksOf(error: unknown): ValidatedHoursWeek[] {
  const weeks =
    error instanceof ApiError ? error.details?.validatedHours : undefined;

  return Array.isArray(weeks) ? (weeks as ValidatedHoursWeek[]) : [];
}
